import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { exchangeTokenAsync } from '../api/kisaan/exchangeTokenAsync';
import { IStreamChannelData } from '../interface/streamChannelList';
import { IStreamLoginResponse } from '../interface/streamLoginResponse';
import { MMKVStoreKeyMap } from '../mmkv/mmkvStoreKeyMap';
import {
  mmkvUserProfilePersistence,
  zustandUserProfileStorage,
} from '../mmkv/mmkvUserProfilePersistence';
import { IRecentSearches, UserProps, UserPropsAzure } from './interface';
import { IIpAddressData } from './interface';
import { IExchangeTokenData, INovuSubscribeData } from '@ninjacart/nc-nk-core';

interface IStreamConfig {
  token_type: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_token_expires_in: number;
  lastTokenGeneratedAt: number;
}

interface IFollowersProfileData {
  userId: string;
  realmId: string;
}

type UserProfileStore = {
  userProfile?: UserProps;
  userProfileAzure: UserPropsAzure | null;
  setUserProfileAzure: (payload: UserPropsAzure) => void;
  setUserProfile: (payload: UserProps) => void;
  _hasHydrated: boolean | null;
  setHydrated: (boolean) => void;
  feedConfig: string | null;
  setFeedConfig: (string) => void;
  clearStorage: () => void;
  isSpinnerVisible: boolean | null;
  setIsSpinnerVisible: (boolean) => void;
  streamConfig: IStreamLoginResponse | null;
  setStreamConfig: (config: IStreamLoginResponse) => void;
  checkAndGetStreamAccessToken: () => void;
  videoChannels: Array<IStreamChannelData>;
  setVideoChannels: (channels: Array<IStreamChannelData>) => void;
  hasUserFeedVideoChannel: () => IStreamChannelData | undefined;
  hasUserQnAVideoChannel: () => IStreamChannelData | undefined;
  deviceId: string | null;
  setDeviceId: (string) => void;
  latitude: any;
  setLatitude: (any) => void;
  longitude: any;
  setLongitude: (any) => void;
  state: string | null;
  setState: (string) => void;
  city: string | null;
  setCity: (string) => void;
  country: string | null;
  setCountry: (string) => void;
  hasUserLoggedIn: () => boolean | null;
  hasUserSkippedLogin: boolean | null;
  setUserSkippedLogin: (isSkipped: boolean) => void;
  recentSearches: IRecentSearches[] | null;
  setRecentSearches: (payload: Array<IRecentSearches>) => void;
  hasNovuLoggedIn: (isLoggedIn: boolean) => void;
  hasGetUserLogin: boolean | null;
  setFcmToken: (string) => void;
  fcmToken: string | null;
  setUserRoles: (roles: string[]) => void;
  setUserPrivileges: (privileges: string[]) => void;
  userRoles: string[];
  userPrivileges: string[];
  truecallerVisibility: boolean | null;
  setTruecallerVisibility: (truecallerVisibility: boolean) => void;
  novuSubscriberData?: INovuSubscribeData;
  setNovuSubscriberData: (novuSubscriberData?: INovuSubscribeData) => void;
  hasRequestedLocationPerm: boolean;
  setHasRequestedLocationPerm: (hasRequested: boolean) => void;
  exchangeTokenSyncReqParams: () => string[];
  runExchangeTokenSync: (
    extStore1?: Function,
    extStore2?: Function
  ) => Promise<IExchangeTokenData | undefined>;
  // runNovuSubscriberSync: () => void;
  // isNovuSubcribeRunning: boolean;
  profileFollowersData: IFollowersProfileData;
  setProfileFollowersData: (profileFollowersData: IFollowersProfileData) => void;
  ipAddressLocation?: IIpAddressData;
  ipAddressData?: IIpAddressData;
  setIpAddressLocation: (ipAddressLocation: IIpAddressData) => void;
};

type State = {
  userProfile?: UserProps;
  userProfileAzure: UserPropsAzure | null;
  _hasHydrated: boolean | null;
  feedConfig: string | null;
  isSpinnerVisible: boolean | null;
  streamConfig: IStreamLoginResponse | null;
  videoChannels: Array<IStreamChannelData>;
  deviceId: string | null;
  latitude: any;
  longitude: any;
  state: string | null;
  city: string | null;
  country: string | null;
  hasUserSkippedLogin: boolean | null;
  recentSearches: IRecentSearches[] | null;
  hasGetUserLogin: boolean | null;
  fcmToken: string | null;
  appStartingTime: any;
  userRoles: string[];
  userPrivileges: string[];
  truecallerVisibility: boolean | null;
  novuSubscriberData?: INovuSubscribeData;
  hasRequestedLocationPerm: boolean;
  isNovuSubcribeRunning: boolean;
  profileFollowersData: IFollowersProfileData;
  ipAddressLocation?: IIpAddressData;
  ipAddressData?: IIpAddressData;
};

let isNovuSubcribeRunning = false;

const initialState: State = {
  userProfileAzure: null,
  _hasHydrated: false,
  feedConfig: null,
  isSpinnerVisible: false,
  streamConfig: null,
  videoChannels: [],
  deviceId: '',
  latitude: null,
  longitude: null,
  state: null,
  city: null,
  country: null,
  hasUserSkippedLogin: null,
  recentSearches: [],
  hasGetUserLogin: null,
  fcmToken: null,
  appStartingTime: null,
  userRoles: [],
  userPrivileges: [],
  truecallerVisibility: false,
  novuSubscriberData: undefined,
  hasRequestedLocationPerm: false,
  profileFollowersData: {
    userId: '',
    realmId: '',
  },
  isNovuSubcribeRunning: false,
  userProfile:undefined,
};

export const useProfileStore = create<UserProfileStore>()(
  persist<UserProfileStore>(
    (set, get) => ({
      ...initialState,
      setUserProfileAzure: (userProfileAzure) =>
        set(() => ({ userProfileAzure: userProfileAzure })),
      setUserProfile: (userProfile) => set(() => ({ userProfile: userProfile })),
      setFeedConfig: (feedConfig) => set(() => ({ feedConfig: feedConfig })),
      setStreamConfig: (config: IStreamConfig) => set(() => ({ streamConfig: config })),
      setHydrated: (isHydrated) => set(() => ({ _hasHydrated: isHydrated })),
      clearStorage: () => {
        mmkvUserProfilePersistence.delete(MMKVStoreKeyMap.userProfile);
        set(initialState);
      },
      setIsSpinnerVisible: (isVisible) => set(() => ({ isSpinnerVisible: isVisible })),
      checkAndGetStreamAccessToken: async () => {},
      setVideoChannels: (channels) => set(() => ({ videoChannels: channels })),
      hasUserFeedVideoChannel: () =>
        get()?.videoChannels.find((x) => x.name === `feed_u${get()?.userProfile?.user?.id}`),
      hasUserQnAVideoChannel: () =>
        get()?.videoChannels.find((x) => x.name === `qna_u${get()?.userProfile?.user?.id}`),
      setDeviceId: (deviceId) => set(() => ({ deviceId: deviceId })),
      setLatitude: (latitude) => set(() => ({ latitude: latitude })),
      setLongitude: (longitude) => set(() => ({ longitude: longitude })),
      setState: (state) => set(() => ({ state: state })),
      setCity: (city) => set(() => ({ city: city })),
      setCountry: (country) => set(() => ({ country: country })),
      hasUserLoggedIn: () => {
        return get()?.userProfile ? true : false;
      },
      setUserSkippedLogin: (isSkipped: boolean) => set(() => ({ hasUserSkippedLogin: isSkipped })),
      setRecentSearches: (payload: Array<IRecentSearches>) =>
        set((state) => ({
          recentSearches:
            [...payload, ...state?.recentSearches!.filter((term) => term !== payload[0])]!.slice(
              0,
              5
            ) ?? [],
        })),
      hasNovuLoggedIn: (isLoggedIn: boolean) => set(() => ({ hasGetUserLogin: isLoggedIn })),
      exchangeTokenSyncReqParams: () => {
        const userProfile = get()?.userProfile;
        const params: string[] = [];
        if (userProfile) {
          if (!get()?.feedConfig) params.push('Feed', 'Qna');
          if (!get()?.streamConfig) params.push('Stream');
        }
        return params;
      },
      setFcmToken: (fcmToken) => set(() => ({ fcmToken: fcmToken })),
      setUserRoles: (roles: string[]) => set(() => ({ userRoles: roles })),
      setUserPrivileges: (priviliges: string[]) => set(() => ({ userPrivileges: priviliges })),
      setNovuSubscriberData: (novuSubscriberData) =>
        set(() => ({ novuSubscriberData: novuSubscriberData })),
      setTruecallerVisibility: (visibility: boolean) =>
        set(() => ({ truecallerVisibility: visibility })),
      setHasRequestedLocationPerm: (hasRequested: boolean) =>
        set(() => ({ hasRequestedLocationPerm: hasRequested })),
      runExchangeTokenSync: async (
        extStore1?: Function,
        extStore2?: Function
      ): Promise<IExchangeTokenData | undefined> => {
        const syncParams = get()?.exchangeTokenSyncReqParams();
        const userProfile = get()?.userProfile;
        if (syncParams?.length && userProfile) {
          const userId = userProfile.user?.id;
          const realmId = userProfile.realm?.id;
          // await exchangeTokenAsync(userProfile?.user?.id, syncParams, extStore1, extStore2);
          const exchangeTokenData = await exchangeTokenAsync(
            get()?.userProfile?.access_token,
            userId,
            realmId,
            syncParams,
            get()?.setFeedConfig,
            get()?.setStreamConfig,
            extStore1,
            extStore2
          );

          return exchangeTokenData;
        }

        // await get()?.runNovuSubscriberSync();
        return undefined;
      },
      // runNovuSubscriberSync: async () => {
      //   if (isNovuSubcribeRunning) {
      //     console.debug('Novu subcriber api already running!!!');
      //     return;
      //   }

      //   isNovuSubcribeRunning = true;
      //   try {
      //     const subscriberId =
      //       get()?.userProfile?.user?.id ?? useAppPersistenceStore.getState()?.deviceId;
      //     const fcmToken = await messaging().getToken();
      //     const res: INovuSubscribeData = await novuSubscriberWithFCMCredentials(
      //       subscriberId,
      //       fcmToken
      //     );
      //     if (res) {
      //       get()?.setNovuSubscriberData(res);
      //     }
      //   } catch (error) {
      //     console.error('Novu subcriber error!!!', error);
      //   } finally {
      //     isNovuSubcribeRunning = false;
      //   }
      // },
      setProfileFollowersData: (profileFollowersData: IFollowersProfileData) =>
        set(() => ({ profileFollowersData })),
      setIpAddressLocation: (ipAddressLocation: IIpAddressData) =>
        set(() => ({ ipAddressLocation })),
    }),
    {
      name: MMKVStoreKeyMap.userProfile,
      storage: createJSONStorage(() => zustandUserProfileStorage),
      // onRehydrateStorage: () => (state, error) => {
      //   if (!error) {
      //     state.setHydrated(true);
      //   }
      // },
    }
  )
);
